import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";
import { toast } from "../../context/TostContext";

const BASE_URL = "/categories";

export const fetchCategories = createAsyncThunk(
    "category/fetchList",
    async (params, { rejectWithValue }) => {
        try {
            const { page, limit, search, type } = params || {};
            const queryParams = new URLSearchParams({
                page: page || 1,
                limit: limit || 10,
                search: search || "",
                type: type || "interest"
            });
            const response = await Axios.get(`${BASE_URL}?${queryParams.toString()}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const createCategory = createAsyncThunk(
    "category/create",
    async (data, { rejectWithValue }) => {
        try {
            const response = await Axios.post(BASE_URL, data, authHeaders());
            toast.success(response.data.message || "Category created successfully");
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const updateCategory = createAsyncThunk(
    "category/update",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await Axios.put(`${BASE_URL}/${id}`, data, authHeaders());
            toast.success(response.data.message || "Category updated successfully");
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const toggleCategoryStatus = createAsyncThunk(
    "category/toggleStatus",
    async ({ id, is_active }, { rejectWithValue }) => {
        try {
            const response = await Axios.put(`${BASE_URL}/${id}`, { is_active }, authHeaders());
            toast.success(response.data.message || "Status updated successfully");
            return { id, is_active, data: response.data };
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchCategoryConfig = createAsyncThunk(
    "category/fetchConfig",
    async (_, { rejectWithValue }) => {
        try {
            const response = await Axios.get(`${BASE_URL}/config`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const deleteCategory = createAsyncThunk(
    "category/delete",
    async (id, { rejectWithValue }) => {
        try {
            const response = await Axios.delete(`${BASE_URL}/${id}`, authHeaders());
            toast.success(response.data.message || "Category deleted successfully");
            return id;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const categorySlice = createSlice({
    name: "category",
    initialState: {
        list: [],
        pagination: { total: 0, page: 1, limit: 10 },
        config: { types: [] },
        isLoading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCategories.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.isLoading = false;
                state.list = action.payload.data || [];
                state.pagination = action.payload.meta || state.pagination;
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(deleteCategory.fulfilled, (state, action) => {
                state.list = state.list.filter((item) => item.id !== action.payload);
            })
            .addCase(toggleCategoryStatus.fulfilled, (state, action) => {
                const { id, is_active } = action.payload;
                const category = state.list.find((item) => item.id === id);
                if (category) {
                    category.is_active = is_active;
                }
            })
            .addCase(fetchCategoryConfig.fulfilled, (state, action) => {
                state.config = action.payload.data || { types: [] };
            });
    },
});

export default categorySlice.reducer;
